import AgyleDialog from '../../modal/agyleDialog';
import AgyleButton from '../../ui/buttons/agyleButton';
import InformationBox from '../../other/informationBox';
import Grid from '@mui/material/Grid';
import EditorImageComponent from '../../editor/editorImage';
import AgyleDictionaryField from '../../ui/selector/agyleDictionaryField';
import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {addNoteForPatient, loadNotesForPatient, noteType} from '../../../store/reducers/notes';
import useOpenState from '../../../hooks/useOpenState';
import {isMobile} from 'react-device-detect';
import {saveAgylePreference} from '../../../store/reducers/preferences';
import {setSnackbar} from '../../../store/reducers/globals';
import {useClinicalNoteStyles} from '../clinicalNotes';
import clsx from 'clsx';
import {AgyleAddTextIcon} from '../../icons/agyle';
import {ctxSplitScreen} from '../../../store/reducers/patientContext';
import Typography from '@mui/material/Typography';

const BACKGROUND_IMAGES = [
    {name: 'Body Back', value: 'bodyBack'},
    {name: 'Body Front', value: 'bodyFront'},
    {name: 'Feet', value: 'feetFront'},
    {name: 'Feet Back', value: 'feetBack'},
];

const BRUSH_COLORS = [
    {id: 1, colour: '#ec1818', label: 'Pain', mode: 'free'},
    {id: 2, colour: '#000000', label: 'Trauma', mode: 'free'},
    {id: 3, colour: '#1858ec', label: 'Callus / Scar', mode: 'free'},
    {id: 4, colour: '#18ec35', label: 'Ulcer / Broken Skin', mode: 'free'},
    {id: 5, colour: 'rgba(0,0,0,0)', label: 'Add text', startIcon: <AgyleAddTextIcon/>, mode: 'text'},
];

const NewBodyMapNote = ({open, onClose, patient, setDefaultNoteType, showAllNotes}) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const classes = useClinicalNoteStyles();

    // Selectors
    const type = useSelector(noteType);
    const splitScreen = useSelector(ctxSplitScreen);

    // Open state
    const [isBodyMapOpen, openBodyMap, closeBodyMap] = useOpenState(false, true);
    const [isBodyMapEditorOpen, openBodyMapEditor, closeBodyMapEditor] = useOpenState(false, true);

    const imageEditorRef = useRef(null);

    // Body map state
    const [selectedBackgroundImage, setSelectedBackgroundImage] = useState(null);
    const [imageBackgroundItem, setImageBackgroundItem] = React.useState(null);

    // Brush state
    const [brushColor, setBrushColor] = React.useState('#ec1818');
    const [brushMode, setBrushMode] = React.useState('free');

    useEffect(() => {
        if (open) {
            setBrushColor('#ec1818');
            setBrushMode('free');
            openBodyMap();
        }
    }, [open]);

    const reloadNotes = () => {
        dispatch(loadNotesForPatient(patient, null, null, !showAllNotes));
    };

    const addUpdateNewNote = (_event, data) => {
        closeBodyMapEditor();
        let newNote = {
            note: data,
            recordType: type,
            patientsInWardsRef: [patient.__id],
            episodeId: patient?.admission?.consultantEpisodeId || null,
        };
        dispatch(saveAgylePreference({defaultNoteType: type}));
        setDefaultNoteType(type);
        dispatch(addNoteForPatient(patient.patient.__id, newNote, patient.__id, false, !showAllNotes));
        reloadNotes();
        onClose(true);
    };

    const submitBackgroundImage = (imgObj) => {
        if (!imgObj?.value) {
            dispatch(setSnackbar({
                type: 'warning',
                message: 'Please select the correct background.',
            }));
            return;
        }
        let width, height, s;

        // eslint-disable-next-line default-case
        switch (imgObj?.value) {
            case 'bodyFront':
                width = 4000;
                height = 3000;
                s = (isMobile ? 0.2 : .25);
                break;
            case 'bodyBack':
                width = 4001;
                height = 3000;
                s = (isMobile ? 0.2 : .25);
                break;
            case 'feetFront':
                width = 4001;
                height = 1000;
                s = .25;
                break;
            case 'feetBack':
                width = 4001;
                height = 1001;
                s = .25;
                break;
        }
        newBodyMapWithImage(imgObj?.value, width, height, s);
        setSelectedBackgroundImage(null);
    };

    const newBodyMapWithImage = (imageName, w, h, s) => {
        closeBodyMap();
        setImageBackgroundItem({
            name: imageName,
            width: w * s,
            height: h * s,
        });
        openBodyMapEditor();
    };

    const addImageNote = () => {
        const data = imageEditorRef.current.getData();
        addUpdateNewNote(null, data);
    };

    const handleClearImage = () => {
        imageEditorRef.current.clear();
        dispatch(setSnackbar({
            type: 'info',
            message: 'Image has been cleared.',
        }));
    };

    return (
        <>
            <AgyleDialog
                title={t('Select Background Image')}
                open={isBodyMapOpen}
                onClose={() => {
                    closeBodyMap();
                    onClose(false);
                }}
                fullWidth
                onSave={() => submitBackgroundImage(selectedBackgroundImage)}
                width="xs"
                submitText={t('Submit')}
                disablePortal={splitScreen.enabled}
            >
                <AgyleDictionaryField
                    onChange={(_, val) => setSelectedBackgroundImage(val)}
                    options={BACKGROUND_IMAGES}
                    value={selectedBackgroundImage}
                    label={t('Background image')}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.name === value.name}
                />
            </AgyleDialog>

            <AgyleDialog
                open={isBodyMapEditorOpen}
                onClose={() => {
                    closeBodyMapEditor();
                    onClose(false);
                }}
                width="xl"
                noPadding
                title={t('Body map editor')}
                closeButtonOnHeader
                fullWidth
                disableSubmit={!isBodyMapEditorOpen}
                onSave={addImageNote}
                buttons={[
                    <AgyleButton
                        key="clear"
                        variant="contained"
                        onClick={handleClearImage}
                    >
                        {t('Clear Image')}
                    </AgyleButton>,
                ]}
                contentStyle={classes.bodyMapBackground}
                disablePortal={splitScreen.enabled}
            >
                <>
                    {brushMode === 'text' &&
                        <InformationBox type="info" noMargin style={{margin: 10, width: 'auto'}}>
                            {t('ENTER to complete text entry.')}
                        </InformationBox>
                    }

                    <Grid container spacing={1}>
                        <Grid
                            item
                            xs={12}
                            md={2}
                            className={classes.palette}
                        >
                            {BRUSH_COLORS.map(brush => (
                                <LeftButton
                                    key={brush.id}
                                    label={t(brush.label)}
                                    color={brush.colour}
                                    onClick={() => {
                                        setBrushColor(brush.colour);
                                        setBrushMode(brush.mode);
                                    }}
                                    selected={brushColor === brush.colour}
                                    startIcon={brush.startIcon || null}
                                />
                            ))}
                        </Grid>

                        <Grid item xs={12} md={10}>
                            <EditorImageComponent
                                ref={imageEditorRef}
                                code=""
                                imageBackground={imageBackgroundItem}
                                currentBrushColor={brushColor}
                                editMode={brushMode}
                                canvasId="bodyMapNote"
                            />
                        </Grid>
                    </Grid>
                </>
            </AgyleDialog>
        </>
    );
};

const LeftButton = (props) => {
    const classes = useClinicalNoteStyles();
    const renderColorCircle = () => (
        <div style={{width: 20, height: 20, borderRadius: 10, backgroundColor: props.color}}/>
    );

    const ref = useRef(null);
    const [isSmall, setIsSmall] = useState(false);

    useEffect(() => {
        if (ref.current) {
            setIsSmall(ref.current?.getBoundingClientRect()?.width < 175);
        }
    }, []);

    return (
        <div ref={ref}>
            <AgyleButton
                onClick={props.onClick}
                variant="text"
                className={clsx(classes.brushButton, props.selected && 'selected')}
                startIcon={props?.startIcon ? props.startIcon : renderColorCircle()}
                justifyContent="flex-start"
                width="100%"
            >
                {!isSmall && props.label}
            </AgyleButton>

            {isSmall &&
                <Typography fontSize={10} fontFamily={600} style={{marginLeft: 5}}>
                    {props.label}
                </Typography>
            }
        </div>
    );
};

export default NewBodyMapNote;